/**
 * Aggregation Utility for Demo Atomic Data
 *
 * Aggregates shift-level atomic records by date, computing:
 * - Sum: netSales, checkCount, fohDollars, bohDollars, fohHours, bohHours
 * - Recalculated: splh (sales per labor hour), laborPct (labor percentage)
 */

/**
 * Aggregates atomic shift-daily records by date
 *
 * @param {Array} atomicRecords - Array of DemoAtomicShiftDaily records
 * @returns {Array} Array of aggregated daily records
 *
 * @example
 * const shiftRecords = [
 *   { date: '2024-01-01', shift: 'BREAKFAST', locationId: 'L1', netSales: 1000, ... },
 *   { date: '2024-01-01', shift: 'LUNCH', locationId: 'L1', netSales: 2000, ... },
 *   { date: '2024-01-01', shift: 'BREAKFAST', locationId: 'L2', netSales: 1500, ... }
 * ];
 * const dailyData = aggregateDailyData(shiftRecords);
 * // Returns one record per date with aggregated metrics
 */
export const aggregateDailyData = (atomicRecords) => {
  if (!atomicRecords || atomicRecords.length === 0) {
    return [];
  }

  // Group records by date
  const grouped = atomicRecords.reduce((acc, record) => {
    const date = record.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(record);
    return acc;
  }, {});

  // Aggregate each date group
  const aggregated = Object.entries(grouped).map(([date, records]) => {
    // Sum additive metrics
    const netSales = sumMetric(records, 'netSales');
    const checkCount = sumMetric(records, 'checkCount');
    const fohHours = sumMetric(records, 'fohHours');
    const bohHours = sumMetric(records, 'bohHours');
    const fohDollars = sumMetric(records, 'fohDollars');
    const bohDollars = sumMetric(records, 'bohDollars');

    // Calculate total hours and labor dollars
    const totalHours = fohHours + bohHours;
    const totalLaborDollars = fohDollars + bohDollars;

    // Recalculate derived metrics
    // SPLH = Total Sales / Total Hours (with division by zero protection)
    const splh = totalHours > 0 ? netSales / totalHours : null;

    // Labor % = Total Labor Dollars / Total Sales * 100 (with division by zero protection)
    const laborPct = netSales > 0 ? (totalLaborDollars / netSales) * 100 : null;

    // Count unique locations
    const locationIds = new Set(records.map(r => r.locationId));
    const locationCount = locationIds.size;

    return {
      date,
      locationCount,
      netSales: roundTo2(netSales),
      checkCount,
      fohHours: roundTo1(fohHours),
      bohHours: roundTo1(bohHours),
      fohDollars: roundTo2(fohDollars),
      bohDollars: roundTo2(bohDollars),
      totalHours: roundTo1(totalHours),
      totalLaborDollars: roundTo2(totalLaborDollars),
      splh: splh !== null ? roundTo2(splh) : null,
      laborPct: laborPct !== null ? roundTo1(laborPct) : null
    };
  });

  // Sort by date ascending
  aggregated.sort((a, b) => a.date.localeCompare(b.date));

  return aggregated;
};

/**
 * Sum a specific metric across records
 * Handles missing/null values gracefully
 *
 * @param {Array} records - Array of records
 * @param {string} field - Field name to sum
 * @returns {number} Sum of field values
 */
const sumMetric = (records, field) => {
  return records.reduce((sum, record) => {
    const value = record[field];
    // Handle null, undefined, or non-numeric values
    if (value == null || isNaN(value)) {
      return sum;
    }
    return sum + Number(value);
  }, 0);
};

/**
 * Round number to 1 decimal place
 * @param {number} num - Number to round
 * @returns {number} Rounded number
 */
const roundTo1 = (num) => {
  return Math.round(num * 10) / 10;
};

/**
 * Round number to 2 decimal places
 * @param {number} num - Number to round
 * @returns {number} Rounded number
 */
const roundTo2 = (num) => {
  return Math.round(num * 100) / 100;
};

export default aggregateDailyData;
